import { useEffect, useState } from 'react';

import {
  GOOD_ENOUGH_M,
  GeolocationStatus,
  useGeolocation as useDeviceLocation,
} from '@/hooks/useGeolocation';
import type { PlaceDetails } from '@/pages/ServiceProviders/ServiceProviderForms';
import { describePosition } from '@/utils/position';

export function useGeolocation() {
  const geo = useDeviceLocation(GOOD_ENOUGH_M);
  const [geoPlace, setGeoPlace] = useState<PlaceDetails | null>(null);
  const [countryCode, setCountryCode] = useState<string | null>(null);

  const coords = geo.status === GeolocationStatus.SUCCESS ? geo.coords : null;

  useEffect(() => {
    if (!coords) return;
    let cancelled = false;

    (async () => {
      const { latitude, longitude } = coords;
      const { placeId, address, formattedAddress } = await describePosition(latitude, longitude);
      if (cancelled) return;

      const code = address.country_code?.toUpperCase();
      if (code) setCountryCode(code);

      setGeoPlace({
        id: placeId?.toString() ?? '',
        formattedAddress,
        location: { latitude, longitude },
        addressComponents: Object.entries(address).map(([key, value]) => ({
          types: [key],
          longText: String(value),
          shortText: String(value),
        })),
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [coords]);

  const issue = geo.status === GeolocationStatus.ERROR ? geo.reason : undefined;
  // Still waiting on a fix, or on the place name for one.
  const locating = geo.status === GeolocationStatus.PENDING || (!!coords && !geoPlace);
  return { geoPlace, countryCode, issue, locating };
}
